import { useEffect, useRef, useState } from 'preact/hooks';
import {
  CONSENT_TEXT,
  CONSENT_VERSION,
  type EstimateRequest,
  type EstimateResponse,
} from '@ks-studio/shared';
import type { WidgetState } from '../state';
import { track } from '../lib/analytics';
import { PRIVACY_URL } from '../lib/env';

export type Step2Props = {
  state: WidgetState;
  update: (patch: Partial<WidgetState>) => void;
  /** Injected for the same reason step 1 injects its two: testability without a network. */
  submit: (req: EstimateRequest) => Promise<EstimateResponse>;
};

const ERROR_ID = 'studio-contact-error';
const BLOCKED_ID = 'studio-contact-blocked';

const TOKEN_DEAD_NOTICE =
  'That address session expired. Please choose the address again.';

/**
 * How a rejected `estimate` should be handled, by code.
 *
 * THREE CODES, THREE DIFFERENT RIGHT ANSWERS, and collapsing them costs a customer
 * either way. `handleEstimate` throws:
 *
 *  - `permission-denied` when the address token does not verify. It is dead for good,
 *    so every retry from this screen fails identically. Go back to step 1.
 *  - `unavailable` when the n8n handoff exhausted its retries. Retrying is genuinely
 *    safe: `submissionId` is stable across the submission and the CRM write is an
 *    upsert filtered on it (ADR-0024), so a retry dedupes rather than duplicating.
 *  - `internal` when n8n understood the payload and refused it. That is our bug, the
 *    next attempt is refused identically, and offering "try again" puts the customer
 *    in a loop that cannot terminate.
 *
 * dac-qualifier uses `failed-precondition` for the dead-token case, so its check does
 * not transfer: copied verbatim it would match nothing here and every expired token
 * would fall through to the generic message.
 */
type Failure = { kind: 'retryable' | 'terminal'; message: string } | { kind: 'restart' };

function classify(err: unknown): Failure {
  const code =
    typeof err === 'object' && err !== null && 'code' in err
      ? (err as { code?: unknown }).code
      : undefined;

  if (code === 'functions/permission-denied') return { kind: 'restart' };
  if (code === 'functions/internal') {
    return {
      kind: 'terminal',
      message:
        'Something went wrong on our end and we did not get your details. Please call us on (518) 478-8365 and we will take them over the phone.',
    };
  }
  // `unavailable`, and anything unrecognised. Defaulting to retryable is the safe
  // direction: the worst case is a customer retrying something that will not work,
  // where defaulting to terminal would tell someone to phone us over a blip.
  return {
    kind: 'retryable',
    message: 'We could not submit that just now. Please try again.',
  };
}

/** Step 2: contact details and consent. Submitting is what creates the lead. */
export function Step2Contact({ state, update, submit }: Step2Props) {
  const [contactConsent, setContactConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Exclude<Failure, { kind: 'restart' }> | null>(
    null,
  );

  /**
   * A ref, not the `busy` state, and the distinction is the whole guard.
   *
   * A second click landing before the re-render that disables the button still runs
   * against the render-N closure, where `busy` reads stale `false`. A ref mutates
   * synchronously, so it is already true when the second click re-enters this
   * function. Submitting twice would fire two leads; they would dedupe in the CRM on
   * `submissionId`, but the customer would pay for two Solar API calls and we would
   * log a phantom.
   */
  const inFlight = useRef(false);

  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    track('step_view', { step: 'contact', submission_id: state.submissionId });
  }, []);

  /**
   * Move focus to the failure message when one appears. `role="alert"` announces it,
   * but announcement is not the same as reachability: the submit button may now be
   * disabled, so a keyboard user's focus is sitting on an element they can no longer
   * act on with no indication of why. WCAG 2.1 AA, master spec section 8.
   */
  useEffect(() => {
    if (failure) errorRef.current?.focus();
  }, [failure]);

  /**
   * What is still outstanding, in field order.
   *
   * A submit button disabled with no stated reason is a dead end: disabled buttons are
   * not focusable, so a customer who cannot see which box is empty has nothing to act
   * on. The button's `aria-describedby` points at this list.
   */
  const missing: string[] = [];
  if (state.firstName.trim() === '') missing.push('your first name');
  if (state.lastName.trim() === '') missing.push('your last name');
  if (!state.email.includes('@')) missing.push('an email address');
  // Required here where dac-qualifier has it optional: `EstimateRequest.contact.phone`
  // is a plain string, and offering it as optional would send '' and put a lead in the
  // CRM that nobody can call.
  if (state.phone.trim() === '') missing.push('a phone number');
  if (!contactConsent) missing.push('the box above ticked');

  const terminal = failure?.kind === 'terminal';
  const ready = missing.length === 0 && state.addressToken !== null && !terminal;

  const onSubmit = async () => {
    if (!state.addressToken) return;
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setFailure(null);

    try {
      const estimate = await submit({
        addressToken: state.addressToken,
        submissionId: state.submissionId,
        contact: {
          firstName: state.firstName.trim(),
          lastName: state.lastName.trim(),
          email: state.email.trim(),
          phone: state.phone.trim(),
        },
        consent: {
          contactConsent: true,
          // The client's clock, at the moment the box was ticked rather than the
          // moment the server received it. ADR-0029.
          contactConsentAt: new Date().toISOString(),
          // What THIS build rendered. The server records it and must not check it,
          // because web and functions deploy on different cadences.
          consentVersion: CONSENT_VERSION,
        },
      });

      // GA4's recommended event name, so it works as a Google Ads conversion with no
      // further mapping. No PII: the allowlist in lib/analytics.ts would strip it.
      track('generate_lead', { submission_id: state.submissionId });
      setBusy(false);
      update({ estimate, step: 'result' });
    } catch (err) {
      const f = classify(err);
      setBusy(false);
      if (f.kind === 'restart') {
        // Back to step 1, carrying the reason. Their contact fields stay in state, so
        // only the address needs choosing again.
        update({
          step: 'address',
          addressToken: null,
          resolved: null,
          notice: TOKEN_DEAD_NOTICE,
        });
        return;
      }
      setFailure(f);
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <section class="step" aria-busy={busy}>
      <h2>How can we reach you?</h2>

      {/* A real form, so Enter submits from any field. `noValidate` keeps the
          messaging in the page rather than in an unstyleable browser bubble that
          would also fire before our own guard runs. */}
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (busy || !ready) return;
          void onSubmit();
        }}
      >
        <div class="grid">
          <label class="field">
            First name{' '}
            <span class="req" aria-hidden="true">
              *
            </span>
            <input
              id="studio-first-name"
              required
              autocomplete="given-name"
              value={state.firstName}
              onInput={(e) => update({ firstName: e.currentTarget.value })}
            />
          </label>
          <label class="field">
            Last name{' '}
            <span class="req" aria-hidden="true">
              *
            </span>
            <input
              id="studio-last-name"
              required
              autocomplete="family-name"
              value={state.lastName}
              onInput={(e) => update({ lastName: e.currentTarget.value })}
            />
          </label>
          <label class="field span2">
            Email{' '}
            <span class="req" aria-hidden="true">
              *
            </span>
            <input
              id="studio-email"
              type="email"
              required
              inputMode="email"
              autocomplete="email"
              value={state.email}
              onInput={(e) => update({ email: e.currentTarget.value })}
            />
          </label>
          <label class="field span2">
            Phone{' '}
            <span class="req" aria-hidden="true">
              *
            </span>
            <input
              id="studio-phone"
              type="tel"
              required
              inputMode="tel"
              autocomplete="tel"
              value={state.phone}
              onInput={(e) => update({ phone: e.currentTarget.value })}
            />
          </label>
        </div>

        <label class="consent">
          <input
            type="checkbox"
            required
            checked={contactConsent}
            onChange={(e) => setContactConsent(e.currentTarget.checked)}
          />
          {/* Rendered FROM THE PINNED CONSTANT, never retyped here. The lead records
              CONSENT_VERSION as proof of what was on screen, so different words here
              than in shared/src/consent.ts would make the pin a lie. ADR-0029. */}
          <span>{CONSENT_TEXT}</span>
        </label>

        <p class="hint">
          See our{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>{' '}
          (opens in a new tab).
        </p>

        {failure && (
          <p class="notice" id={ERROR_ID} role="alert" tabIndex={-1} ref={errorRef}>
            {failure.message}
          </p>
        )}

        <div class="actions">
          <button
            type="button"
            disabled={busy}
            onClick={() => update({ step: 'address' })}
          >
            Back
          </button>
          <button
            type="submit"
            class="primary"
            disabled={busy || !ready}
            aria-describedby={
              [missing.length > 0 ? BLOCKED_ID : null, failure ? ERROR_ID : null]
                .filter(Boolean)
                .join(' ') || undefined
            }
          >
            {busy ? 'Checking…' : 'See my estimate'}
          </button>
        </div>

        {/* On `missing`, not on `!ready`. `ready` is also false when the token is
            absent or the failure is terminal, and neither is something the customer
            can act on from this list, so it must not render an empty "Still needed:
            ." line. */}
        {missing.length > 0 && (
          <p class="blocked-reason" id={BLOCKED_ID}>
            Still needed: {missing.join(', ')}.
          </p>
        )}
      </form>

      <p class="sr-only" role="status">
        {busy ? 'Submitting your details.' : ''}
      </p>
    </section>
  );
}
